/**
 * `/settings/audit` — tenant audit log (M3.8).
 *
 * Filterable by event type. Owner/admin only — non-admins see a permission-denied message.
 * Cursor-paginated via `before` (ISO timestamp) — older pages append on Load more.
 */
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useMe } from "@/hooks/use-me";
import { useAuditLog } from "@/hooks/use-audit";
import { formatTimestamp } from "@/lib/formatters";

const READ_ROLES = new Set(["owner", "admin"]);
const PAGE_LIMIT = 50;

const EVENT_TYPE_COLORS: Record<string, string> = {
  "agent.created": "bg-green-100 text-green-800",
  "agent.updated": "bg-blue-100 text-blue-800",
  "agent.deleted": "bg-red-100 text-red-800",
  "secret.written": "bg-yellow-100 text-yellow-800",
  "secret.deleted": "bg-red-100 text-red-800",
  "member.invited": "bg-purple-100 text-purple-800",
  "run.dispatched": "bg-gray-100 text-gray-700",
};

const EVENT_TYPES = [
  { value: "", label: "All events" },
  { value: "agent.created", label: "Agent created" },
  { value: "agent.updated", label: "Agent updated" },
  { value: "agent.deleted", label: "Agent deleted" },
  { value: "secret.written", label: "Secret written" },
  { value: "member.invited", label: "Member invited" },
  { value: "run.dispatched", label: "Run dispatched" },
];

export function SettingsAuditPage() {
  const me = useMe();
  const [eventType, setEventType] = useState<string>("");
  const [before, setBefore] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<import("@simpliwork/api-schema").AuditEvent[]>([]);
  const isLoadMoreRef = useRef(false);

  const canRead = READ_ROLES.has(me.data?.role ?? "");
  const auditLogParams = canRead
    ? {
        ...(eventType ? { eventType } : {}),
        limit: PAGE_LIMIT,
        ...(before !== undefined && { before }),
      }
    : undefined;
  const auditLog = useAuditLog(auditLogParams);

  useEffect(() => {
    if (!auditLog.data) return;
    if (isLoadMoreRef.current) {
      setAllItems((prev) => [...prev, ...(auditLog.data?.items ?? [])]);
      isLoadMoreRef.current = false;
    } else {
      setAllItems(auditLog.data.items);
    }
  }, [auditLog.data]);

  function handleEventTypeChange(v: string) {
    setEventType(v);
    setBefore(undefined);
    setAllItems([]);
    isLoadMoreRef.current = false;
  }

  function handleLoadMore() {
    if (!auditLog.data?.next_cursor) return;
    isLoadMoreRef.current = true;
    setBefore(auditLog.data.next_cursor);
  }

  const isInitialLoading = auditLog.isPending && allItems.length === 0;
  const isFetchingMore = auditLog.isFetching && allItems.length > 0;

  if (!me.isPending && !canRead) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Audit Log</h1>
        <p className="text-sm text-muted-foreground">
          Only owners and admins can view the audit log.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          A record of significant events in your workspace.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle>Events</CardTitle>
            <CardDescription>
              {allItems.length > 0
                ? `${allItems.length} event${allItems.length === 1 ? "" : "s"} loaded`
                : "Most recent first."}
            </CardDescription>
          </div>
          <select
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            value={eventType}
            onChange={(e) => handleEventTypeChange(e.target.value)}
          >
            {EVENT_TYPES.map((et) => (
              <option key={et.value} value={et.value}>
                {et.label}
              </option>
            ))}
          </select>
        </CardHeader>
        <CardContent>
          {isInitialLoading ? (
            <div className="space-y-3">
              {(["a", "b", "c", "d", "e"] as const).map((k) => (
                <Skeleton key={k} className="h-12 w-full" />
              ))}
            </div>
          ) : allItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No audit events yet.
            </p>
          ) : (
            <>
              <div className="divide-y">
                {allItems.map((event) => (
                  <div key={event.id} className="flex items-start gap-3 py-3">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold whitespace-nowrap mt-0.5 ${EVENT_TYPE_COLORS[event.event_type] ?? "bg-gray-100 text-gray-700"}`}
                    >
                      {event.event_type}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{event.actor_display}</span>
                        {event.resource_type && event.resource_id && (
                          <span className="text-muted-foreground">
                            {" "}
                            · {event.resource_type}{" "}
                            <span className="font-mono text-xs">{event.resource_id.slice(0, 8)}</span>
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(event.occurred_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {auditLog.data?.next_cursor && (
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={isFetchingMore}
                    data-testid="audit-load-more"
                  >
                    {isFetchingMore ? "Loading…" : "Load more"}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
